import React, { useEffect, useState } from "react";
import { Card, Col, Row, Spinner, Button } from "reactstrap";
import { CustomInput } from "../../Common/Components/Input/CustomInput";
import { CustomSubmitButton } from "../../Common/Components/Input/CustomSubmitButton";
import useApiCallService from "../../Common/Helpers/ApiCall";
import { FlashMessage } from "../../Common/Helpers/FlashMessage";
import { ADMIN_PATHS } from "../../config";
import { useNavigate } from "react-router-dom";
import { CustomButton } from "../../Common/Components/Button/CustomButton";
import { useTranslation } from "react-i18next";
import { CustomReload } from "../../AppSystem/Helpers/CustomReload";
import { CustomModal } from "../../Common/Components/Modal/CustomModal";
import AdminRequestCostBearerCreate from "../Models/Request/AdminRequestCostBearerCreate";
import CostBearerModel from "../Models/Response/CostBearerModel";
import { error } from "jquery";
import { useHeader } from "../../Auth/Components/Nav/HeaderContext";
import { CheckMobile } from "../../AppSystem/Helpers/CheckMobile";

export function AdminCostBearerCreate() {
    const { t } = useTranslation();
    const { ApiCall } = useApiCallService();
    const navigate = useNavigate();
    const isMobile = CheckMobile(998);
    const { setPageTitle, setPageTitleKey, setOnBack } = useHeader();

    useEffect(() => {
        setPageTitle(t('costBearer.create'));
        setPageTitleKey('costBearer.create');
        setOnBack(() => { navigate(ADMIN_PATHS.ADMIN_COST_BEARER); });
        return () => { setPageTitle(''); setPageTitleKey(''); setOnBack(null); };
    }, [t]);

    const [costBearer, setCostBearer] = useState(new AdminRequestCostBearerCreate());
    const [responseCostBearer, setResponseCostBearer] = useState(new CostBearerModel());

    const [displaySpinner, setDisplaySpinner] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    // validation state
    const [fieldErrors, setFieldErrors] = useState({});
    const [touched, setTouched] = useState({});

    // MODALS
    const [displaySendModal, setDisplaySendModal] = useState(false);

    // GET ID IF EXISTS FOR DRAFT
    const urlParams = new URLSearchParams(window.location.search);
    const id = urlParams.get('id') ?? "";

    // yup schema from model
    const schema = AdminRequestCostBearerCreate.getAttributes(null, true);

    // handle per-field blur validation
    const handleBlur = async (name) => {
        setTouched(prev => ({ ...prev, [name]: true }));
        try {
            await schema.validateAt(name, costBearer);
            // valid -> clear field error
            setFieldErrors(prev => {
                const copy = { ...prev };
                delete copy[name];
                return copy;
            });
        } catch (err) {
            setFieldErrors(prev => ({ ...prev, [name]: err.message }));
        }
    };

    const handleChangeCostBearer = (e, name) => {
        let value;

        if (e && e.target) {
            if (e.target.type === "checkbox") value = e.target.checked;
            else value = e.target.value;
        } else if (e && e.value !== undefined) {
            // support for select-like custom inputs
            value = e.value;
        } else {
            value = e ?? "";
        }

        // update model
        setCostBearer((prevFormData) => ({
            ...prevFormData,
            [name]: value
        }));

        // clear error for this field as user types
        setFieldErrors(prev => {
            const copy = { ...prev };
            delete copy[name];
            return copy;
        });
    };

    const handleValidation = async () => {
        setDisplaySpinner(true);

        try {
            await schema.validate(costBearer, { abortEarly: false });

            const payload = new URLSearchParams();
            payload.append("name", costBearer.name ?? "");
            payload.append("value", costBearer.value ?? "");
            payload.append("showLabel", costBearer.showLabel ?? "");
            payload.append("message", costBearer.message ?? "");
            payload.append("includedInAGI", costBearer.includedInAGI ? "true" : "false");

            const apiCallOptions = {
                method: "post",
                url: `/costbearer?${payload.toString()}`,
            };

            try {
                await ApiCall(apiCallOptions, setResponseCostBearer, setErrorMessage);
            } catch (error) {
                setDisplaySpinner(false);
                console.error(error);
                FlashMessage(t('validationMessage.sendError'), 'flash-messageError');
            }
        } catch (err) {
            // Yup validation failed -> collect field errors
            const newErrors = {};
            if (err && err.inner && err.inner.length > 0) {
                err.inner.forEach(e => {
                    if (e.path) newErrors[e.path] = e.message;
                });
            } else if (err && err.path) {
                newErrors[err.path] = err.message;
            }

            setFieldErrors(newErrors);
            const newTouched = { ...touched };
            Object.keys(newErrors).forEach(k => { newTouched[k] = true; });
            setTouched(newTouched);

            setDisplaySpinner(false);
        }
    };

    useEffect(() => {
        if (errorMessage !== '') {
            if (errorMessage.includes('validationMessage.kbarExists') || errorMessage.includes('validationMessage.nameExists')) {
                FlashMessage(t(errorMessage), 'flash-messageError');
            } else {
                FlashMessage(t('validationMessage.sendError'), 'flash-messageError');
            }
            setDisplaySpinner(false);
            setErrorMessage('');
            setTimeout(() => { CustomReload(); }, 4800);
        }
    }, [errorMessage, t]);

    // REDIRECT TO INFO PAGE
    useEffect(() => {
        if (responseCostBearer && responseCostBearer.id !== "") {
            FlashMessage(t('validationMessage.costbearerSuccessfully'), 'flash-messageSuccess');
            setTimeout(() => {
                navigate(`${ADMIN_PATHS.ADMIN_COST_BEARER}`);
            }, 2000);
        }
    }, [responseCostBearer, navigate, t]);

    // GET COST BEARER IF EXISTS (DRAFT)
    useEffect(() => {
        if (id !== "") {
            const handleApiResponse = (apiResponse) => {
                setCostBearer(new AdminRequestCostBearerCreate(apiResponse));
            };
            const fetchData = async () => {
                try {
                    const apiCallOptions = {
                        method: "get",
                        url: `/costbearer?id=${id}`
                    };
                    await ApiCall(apiCallOptions, handleApiResponse);
                } catch (error) {
                    console.log('error', error);
                }
            };
            fetchData();
        }
    }, [id, ApiCall]);

    // CHECK REQUIRED INPUTS (message NOT required)
    const CheckRequired = () => {
        // trigger validation UI on blur for both fields
        handleBlur('name');
        handleBlur('value');

        const nameOk = !!costBearer.name && costBearer.name.trim() !== "";
        const valueOk = !!costBearer.value && costBearer.value.trim() !== "";

        return nameOk && valueOk;
    };

    const modalSendContent = (
        <div id="sendMessageDiv">
            {t('costBearer.costbearerModalSend')}
        </div>
    );

    const modalSendFooter = (
        <>
            <Button className="box-green border-0" id="sendButton" onClick={async () => {
                setDisplaySendModal(false);
                await handleValidation();
            }}>
                {t('button.create')}
            </Button>
            <Button className="box-red border-0" onClick={() => {
                setDisplaySendModal(false);
            }}>
                {t('button.cancel')}
            </Button>
        </>
    );

    const renderFieldErrorAbove = (fieldName) => {
        const err = fieldErrors[fieldName];
        if (!err || !touched[fieldName]) return null;
        const translated = typeof err === 'string' ? t(err) : err;
        return (
            <div className={`error tooltip-custom-normal position-absolute ${err ? 'opacity-1' : 'opacity-0'}`}
                style={{color: '#f56e6e'}}>
                {translated}
            </div>
        );
    };

    return (
        <>
            <CustomModal
                isOpen={displaySendModal}
                toggle={() => setDisplaySendModal(!displaySendModal)}
                body={modalSendContent}
                footer={modalSendFooter}
            />
            {!isMobile && (
                <Col>
                    <CustomButton
                        text={t('button.back')}
                        icon={'bx bx-chevron-left'}
                        buttonSize={'small'}
                        customClasses={'mb-3 ms-3'}
                        onClick={() => {
                            navigate(ADMIN_PATHS.ADMIN_COST_BEARER);
                        }}
                    />
                </Col>
            )}
            <Row className="pb-5 px-0 mx-0">
                <Col lg={6} className="px-0 mx-0">
                    <Card className="card-backend p-4">
                        <Row>
                            <Col xs='auto'>
                                <h2 className='rokkitt-font'>{t('costBearer.create')}</h2>
                            </Col>
                        </Row>
                        <Row>
                            <Col lg={12}>
                                <div className={'mb-3 position-relative'} style={{ paddingTop: '10px' }}>
                                    {renderFieldErrorAbove('value')}
                                    <CustomInput
                                        model={AdminRequestCostBearerCreate}
                                        attribute='value'
                                        data={costBearer.value}
                                        customChange={(e) => handleChangeCostBearer(e, 'value')}
                                        inputProps={{ id: 'value', onBlur: () => handleBlur('value') }}
                                    />
                                </div>
                            </Col>

                            <Col lg={12}>
                                <div className={'mb-3 position-relative'} style={{ paddingTop: '10px' }}>
                                    {renderFieldErrorAbove('name')}
                                    <CustomInput
                                        model={AdminRequestCostBearerCreate}
                                        attribute='name'
                                        data={costBearer.name}
                                        customChange={(e) => handleChangeCostBearer(e, 'name')}
                                        inputProps={{ id: 'name', onBlur: () => handleBlur('name') }}
                                    />
                                </div>
                            </Col>

                            <Col lg={12}>
                                <div className="mb-3">
                                    <label>{t('costBearer.message')}</label>
                                    <textarea
                                        className="form-control"
                                        data={costBearer.message}
                                        onChange={(e) => handleChangeCostBearer(e, "message")}
                                        style={{ resize: "vertical", minHeight: "60px", width: "100%", overflow: "auto" }}
                                        rows={2}
                                    />
                                </div>
                            </Col>

                            <Col lg={12}>
                                <div className="form-check mb-3">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="isActiveCheckbox"
                                        checked={!!costBearer.includedInAGI}
                                        onChange={(e) =>
                                            setCostBearer((prev) => ({
                                                ...prev,
                                                includedInAGI: e.target.checked,
                                            }))
                                        }
                                    />
                                    <label className="form-check-label" htmlFor="isActiveCheckbox">
                                        {t("costBearer.includedInAGILabel")}
                                    </label>
                                </div>
                            </Col>

                            <Col lg={12} className="text-end pt-5">
                                {displaySpinner === true ? (
                                    <>
                                        {t('validationMessage.creatingCostBearer')} <Spinner color='success' className="me-3" />
                                    </>
                                ) : ''}
                                <CustomSubmitButton
                                    text={t('button.create')}
                                    id='submitCostBearer'
                                    icon='bx bx-save pe-2'
                                    customClick={async () => {
                                        if (displaySpinner) return;

                                        try {
                                            await schema.validate(costBearer, { abortEarly: false });

                                            setDisplaySendModal(true);
                                        } catch (err) {
                                            const newErrors = {};
                                            if (err && err.inner && err.inner.length > 0) {
                                                err.inner.forEach(e => {
                                                    if (e.path) newErrors[e.path] = e.message;
                                                });
                                            } else if (err && err.path) {
                                                newErrors[err.path] = err.message;
                                            }

                                            setFieldErrors(newErrors);

                                            const newTouched = { ...touched };
                                            Object.keys(newErrors).forEach(k => { newTouched[k] = true; });
                                            setTouched(newTouched);
                                        }
                                    }}
                                />
                            </Col>
                        </Row>
                    </Card>
                </Col>
            </Row>
        </>
    );
}

export default AdminCostBearerCreate;
